import { useMemo, useState } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { alertActionsFor } from '@project/contracts'
import {
  Button, Field, Icon, Input, MenuButton, Modal, Notice, Panel, Select,
} from '../../design/index.js'
import { useApi } from '../../app/api.js'
import {
  adminApi, type RouteAlert, type RouteInput, type RouteView, type ScenarioAction, type ScenarioCondition, type ScenarioView,
} from '../../app/admin-api.js'
import { ErrorBlock, Loading, messageFor } from '../../framework/feedback.js'
import { formatDate } from '../../framework/format.js'
import { AdminList, textMatch, type AdminColumn } from './AdminList.js'
import { TeamBoard } from '../../framework/TeamBoard.js'
import { ChipChoice, MultiLookup } from './pickers.js'
import './admin.css'

/**
 * Approval routes as the reference keeps them (WF-006 … WF-013).
 *
 * A numbered list — Workflow No, Document, Companies, Status — with filters
 * and export; a record page per route with an Action menu (Add companies,
 * Update override users, Move to archive, Copy this workflow); and an editor
 * whose steps hold scenarios, can be moved up and down, and may each finish
 * the route.
 */

const STATUS_WORDS: Readonly<Record<RouteView['status'], string>> = { LIVE: 'Live', DRAFT: 'Draft', ARCHIVED: 'Archived' }
const ACTION_WORDS: Readonly<Record<ScenarioAction, string>> = {
  ApproveAndContinue: 'Approve and Continue',
  ApproveAndFinish: 'Approve and Finish',
}
const FIELD_WORDS: Readonly<Record<ScenarioCondition['field'], string>> = {
  total: 'Total amount', projectId: 'Project', partyId: 'Party', categoryId: 'Item category',
}
const OP_WORDS: Readonly<Record<string, string>> = { gt: 'more than', gte: 'at least', lt: 'less than', lte: 'at most', in: 'is one of' }
const ENTITY_FOR: Readonly<Record<string, string>> = { projectId: 'project', partyId: 'party', categoryId: 'item_category' }

const routesKey = (companyId: string) => ['adminRoutes', companyId]

export const WorkflowsPage = () => {
  const api = useApi()
  const admin = adminApi(api)
  const [openId, setOpenId] = useState<string | null>(null)
  const [editing, setEditing] = useState<Draft | null>(null)
  const [creating, setCreating] = useState(false)

  const routes = useQuery({ queryKey: routesKey(api.companyId), queryFn: () => admin.routes() })
  const types = useQuery({ queryKey: ['adminWorkflowTypes'], queryFn: () => admin.routeTypes(), staleTime: 600_000 })

  if (routes.isLoading) return <Loading />
  if (routes.error) return <ErrorBlock error={routes.error} />
  const rows = routes.data ?? []
  const open = rows.find((r) => r.id === openId) ?? null

  if (editing) return <RouteEditor draft={editing} onDone={(id) => { setEditing(null); if (id) setOpenId(id) }} />
  if (open) return <RouteRecord route={open} onBack={() => setOpenId(null)} onEdit={(d) => setEditing(d)} />

  const companies = [...new Set(rows.flatMap((r) => r.companies))]
  const columns: ReadonlyArray<AdminColumn<RouteView>> = [
    { key: 'wfNo', label: 'Workflow No', width: 140, locked: true, render: (r) => <span className="num">{r.wfNo ?? '—'}</span> },
    { key: 'docTypeLabel', label: 'Document', render: (r) => <><div>{r.docTypeLabel}</div><div className="cell-sub">{r.name} · version {r.version}</div></> },
    { key: 'companies', label: 'Companies', width: 240, render: (r) => r.companies.join(', ') },
    { key: 'status', label: 'Status', width: 110, render: (r) => STATUS_WORDS[r.status] },
    { key: 'overrideUsers', label: 'Override Users', width: 180, optional: true, render: (r) => r.overrideUsers.join(', ') || '—' },
    { key: 'createdAt', label: 'Created On', width: 130, optional: true, render: (r) => (r.createdAt ? formatDate(r.createdAt.slice(0, 10)) : '—') },
    { key: 'createdByName', label: 'Created By', width: 150, optional: true, render: (r) => r.createdByName ?? '—' },
    { key: 'description', label: 'Description', optional: true, render: (r) => r.description ?? '—' },
  ]

  return (
    <>
      <AdminList
        title="All Workflows"
        scope="admin:workflows"
        rows={rows}
        columns={columns}
        getKey={(r) => r.id}
        onRowClick={(r) => setOpenId(r.id)}
        actions={
          <Button kind="primary" icon={<Icon name="plus" size={18} />} onClick={() => setCreating(true)}>Create Workflow</Button>
        }
        filters={[
          { key: 'docType', label: 'Document', options: (types.data ?? []).map((t) => ({ value: t.key, label: t.label })), matches: (r, v) => r.docType === v },
          { key: 'company', label: 'Companies', options: companies.map((c) => ({ value: c, label: c })), matches: (r, v) => r.companies.includes(v) },
          { key: 'wfNo', label: 'Workflow No', matches: (r, v) => textMatch(r.wfNo, v) },
          { key: 'status', label: 'Status', options: (['LIVE', 'DRAFT', 'ARCHIVED'] as const).map((s) => ({ value: s, label: STATUS_WORDS[s] })), matches: (r, v) => r.status === v },
        ]}
        exportColumns={[
          { header: 'Workflow No', value: (r) => r.wfNo },
          { header: 'Document', value: (r) => r.docTypeLabel },
          { header: 'Companies', value: (r) => r.companies.join(', ') },
          { header: 'Status', value: (r) => STATUS_WORDS[r.status] },
          { header: 'Override Users', value: (r) => r.overrideUsers.join(', ') },
          { header: 'Created On', value: (r) => r.createdAt?.slice(0, 10) ?? null },
          { header: 'Created By', value: (r) => r.createdByName },
        ]}
      />
      {creating ? (
        <Modal title="Create Workflow" onClose={() => setCreating(false)}
          actions={<Button kind="ghost" onClick={() => setCreating(false)}>Cancel</Button>}>
          <Field label="Document" required hint="It cannot be changed once the workflow is created.">
            <Select value="" placeholder="Choose a document type"
              options={(types.data ?? []).map((t) => ({ value: t.key, label: t.label }))}
              onChange={(docType) => {
                const t = (types.data ?? []).find((x) => x.key === docType)
                if (!t) return
                setCreating(false)
                setEditing(blankDraft(t.key, t.label))
              }} />
          </Field>
        </Modal>
      ) : null}
    </>
  )
}

/* ---- one route ----------------------------------------------------------- */

const RouteRecord = ({ route, onBack, onEdit }: { route: RouteView; onBack: () => void; onEdit: (d: Draft) => void }) => {
  const api = useApi()
  const admin = adminApi(api)
  const client = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [dialog, setDialog] = useState<'companies' | 'override' | 'copy' | null>(null)
  const users = useQuery({ queryKey: ['adminUsers', api.companyId], queryFn: () => api.adminUsers() })
  const companies = useQuery({ queryKey: ['adminCompanies', api.companyId], queryFn: () => api.adminCompanies() })

  const act = useMutation({
    mutationFn: (fn: () => Promise<RouteView[]>) => fn(),
    onSuccess: (next) => {
      client.setQueryData(routesKey(api.companyId), next)
      setDialog(null)
      setError(null)
    },
    onError: (err) => setError(messageFor(err)),
  })

  const userName = (id: string): string => users.data?.find((u) => u.id === id)?.name ?? id
  /** The copy opens in the draft editor, where it is changed and then published. */
  const copy = useMutation({
    mutationFn: (into: { docType?: string; companyId?: string }) => admin.copyRoute(route.id, into),
    onSuccess: (next) => {
      const before = new Set((client.getQueryData<RouteView[]>(routesKey(api.companyId)) ?? []).map((r) => r.id))
      client.setQueryData(routesKey(api.companyId), next)
      setDialog(null)
      setError(null)
      const made = next.find((r) => !before.has(r.id) && r.status === 'DRAFT')
      if (made) onEdit(draftOf(made))
    },
    onError: (err) => setError(messageFor(err)),
  })
  const editable = route.status !== 'ARCHIVED'
  const items = [
    ...(route.status === 'DRAFT' ? [{ key: 'publish', label: 'Publish', onSelect: () => act.mutate(() => admin.setRouteStatus(route.id, 'LIVE')) }] : []),
    ...(route.status === 'ARCHIVED' ? [{ key: 'restore', label: 'Put back into service', onSelect: () => act.mutate(() => admin.setRouteStatus(route.id, 'LIVE')) }] : []),
    // A live route is changed the reference's way: copy it, edit the draft, publish.
    ...(route.status === 'DRAFT' ? [{ key: 'edit', label: 'Edit draft', onSelect: () => onEdit(draftOf(route)) }] : []),
    ...(route.companyId === api.companyId && editable ? [
      { key: 'companies', label: 'Add companies', onSelect: () => setDialog('companies') },
      { key: 'override', label: 'Update Override Users', onSelect: () => setDialog('override') },
    ] : []),
    { key: 'copy', label: 'Copy this workflow', onSelect: () => setDialog('copy') },
    ...(route.status !== 'ARCHIVED' && route.companyId === api.companyId
      ? [{ key: 'archive', label: 'Move to archive', danger: true, onSelect: () => act.mutate(() => admin.setRouteStatus(route.id, 'ARCHIVED')) }]
      : []),
  ]
  const alertWords = alertActionsFor(route.docType)

  return (
    <>
      <button type="button" className="crumb link-button" onClick={onBack}><Icon name="left" size={15} /> All Workflows</button>
      <div className="doc-head">
        <div>
          <h2 className="page-title">{route.wfNo} <span className="chip">{STATUS_WORDS[route.status]}</span></h2>
          <p className="page-subtitle">{route.docTypeLabel} · {route.name} · version {route.version}
            {route.inFlight > 0 ? ` · ${route.inFlight} in review` : ''}</p>
        </div>
        <div className="doc-actions"><MenuButton label="Action" items={items} /></div>
      </div>
      {error ? <div style={{ margin: '12px 0' }}><Notice kind="bad">{error}</Notice></div> : null}

      <Panel title="General Info">
        <dl className="kv">
          <dt>Document</dt><dd>{route.docTypeLabel}</dd>
          <dt>Description</dt><dd>{route.description ?? '—'}</dd>
        </dl>
      </Panel>

      <Panel title="Companies">
        <p>{route.companies.join(', ')}</p>
      </Panel>

      <Panel title="Approval Steps" aside={route.status === 'LIVE' ? 'Live steps are read-only. To change them, copy this workflow and publish the draft' : undefined}>
        {route.stages.map((stage) => (
          <div key={stage.order} className="route-step">
            <div className="route__title">{stage.order}. {stage.name}</div>
            <ScenarioTable scenarios={scenariosOf(stage)} />
          </div>
        ))}
      </Panel>

      <Panel title="Override">
        <p>{route.overrideUsers.length > 0 ? route.overrideUsers.join(', ') : 'No override users.'}</p>
        <p className="helper">Override users may sign any step; signing as one finishes the route.</p>
      </Panel>

      {/* Every action that can alert is listed, so an admin sees at a glance which tell nobody. */}
      <Panel title="Alerts">
        <table className="scenario-table">
          <thead><tr><th>#</th><th>On Action</th><th>Project Roles</th><th>Roles</th><th>Users</th></tr></thead>
          <tbody>
            {alertRows(alertWords, route.alerts).map(({ label, alert: a }, i) => (
              <tr key={a.action}>
                <td>{i + 1}</td>
                <td>{label}</td>
                <td><RecordNames entity="project_role" ids={a.projectRoleIds} /></td>
                <td><RecordNames entity="app_role" ids={a.roleIds} /></td>
                <td>{a.userIds.map(userName).join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      {/* Why the route is what it is, kept beside it. */}
      {route.companyId === api.companyId ? <TeamBoard record={{ type: 'workflow_def', id: route.id }} /> : null}

      {dialog === 'companies' ? (
        <PickDialog
          title="Add companies"
          hint="The route applies in these companies as well. A company's own live route wins over a shared one."
          options={(companies.data ?? []).filter((c) => c.id !== route.companyId).map((c) => ({ value: c.id, label: c.name }))}
          initial={route.appliesTo}
          busy={act.isPending}
          onClose={() => setDialog(null)}
          onSave={(ids) => act.mutate(() => admin.setRouteReach(route.id, { appliesTo: ids }))}
        />
      ) : null}
      {dialog === 'override' ? (
        <PickDialog
          title="Update Override Users"
          hint="Takes effect at once, on documents already in review too. No new version is published."
          options={(users.data ?? []).filter((u) => u.active).map((u) => ({ value: u.id, label: u.name }))}
          initial={route.overrideUserIds}
          busy={act.isPending}
          onClose={() => setDialog(null)}
          onSave={(ids) => act.mutate(() => admin.setRouteReach(route.id, { overrideUserIds: ids }))}
        />
      ) : null}
      {dialog === 'copy' ? (
        <CopyDialog route={route} busy={copy.isPending} onClose={() => setDialog(null)}
          onCopy={(into) => copy.mutate(into)} />
      ) : null}
    </>
  )
}

const PickDialog = ({ title, hint, options, initial, busy, onClose, onSave }: {
  title: string; hint: string
  options: ReadonlyArray<{ value: string; label: string }>
  initial: readonly string[]
  busy: boolean
  onClose: () => void
  onSave: (ids: string[]) => void
}) => {
  const [chosen, setChosen] = useState<string[]>([...initial])
  return (
    <Modal title={title} onClose={onClose}
      actions={<><Button kind="ghost" onClick={onClose}>Cancel</Button><Button kind="primary" disabled={busy} onClick={() => onSave(chosen)}>Save</Button></>}>
      <p className="helper">{hint}</p>
      <ChipChoice label={title} options={options} value={chosen} onChange={setChosen} />
    </Modal>
  )
}

const CopyDialog = ({ route, busy, onClose, onCopy }: {
  route: RouteView; busy: boolean; onClose: () => void; onCopy: (into: { docType?: string; companyId?: string }) => void
}) => {
  const api = useApi()
  const types = useQuery({ queryKey: ['adminWorkflowTypes'], queryFn: () => adminApi(api).routeTypes(), staleTime: 600_000 })
  const companies = useQuery({ queryKey: ['adminCompanies', api.companyId], queryFn: () => api.adminCompanies() })
  const [docType, setDocType] = useState(route.docType)
  const [companyId, setCompanyId] = useState(api.companyId)
  return (
    <Modal title="Copy this workflow" onClose={onClose}
      actions={<><Button kind="ghost" onClick={onClose}>Cancel</Button>
        <Button kind="primary" disabled={busy} onClick={() => onCopy({ docType, companyId })}>Copy as a draft</Button></>}>
      <p className="helper">The copy is a new draft with its own number. Publish it to put it into service.</p>
      <Field label="Document">
        <Select value={docType} onChange={setDocType} options={(types.data ?? []).map((t) => ({ value: t.key, label: t.label }))} />
      </Field>
      <Field label="Company">
        <Select value={companyId} onChange={setCompanyId} options={(companies.data ?? []).map((c) => ({ value: c.id, label: c.name }))} />
      </Field>
    </Modal>
  )
}

/**
 * The alert rows of a route: one per action the document can alert on, in the
 * reference's order, then any stored action the document no longer offers.
 */
const alertRows = (
  actions: ReadonlyArray<{ action: string; label: string }>, stored: readonly RouteAlert[],
): Array<{ label: string; alert: RouteAlert }> => [
  ...actions.map((w) => ({
    label: w.label,
    alert: stored.find((a) => a.action === w.action) ?? { action: w.action, projectRoleIds: [], roleIds: [], userIds: [] },
  })),
  ...stored.filter((a) => !actions.some((w) => w.action === a.action)).map((a) => ({ label: a.action, alert: a })),
]

/** Record names for a list of ids, read through the lookup cache the chips share. */
const useRecordNames = (entity: string, ids: readonly string[]): string[] => {
  const api = useApi()
  const rows = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['lookupCard', entity, id],
      queryFn: async () => (await api.lookupById(entity, id)) ?? null,
      staleTime: 60_000,
    })),
  })
  return ids.map((id, i) => rows[i]?.data?.label ?? (rows[i]?.isLoading ? '…' : id))
}

const RecordNames = ({ entity, ids }: { entity: string; ids: readonly string[] }) => {
  const names = useRecordNames(entity, ids)
  return <>{names.join(', ')}</>
}

/** "Umapathy C and 1 more", with everyone named in the tooltip (the reference's Who can approve). */
const Approvers = ({ ids }: { ids: readonly string[] }) => {
  const names = useRecordNames('employee', ids)
  const [first, ...rest] = names
  return <span title={names.join(', ')}>{rest.length > 0 ? `${first} and ${rest.length} more` : first}</span>
}

/** A stage stored before scenarios is one scenario: Always, with the step's own action. */
const scenariosOf = (stage: RouteView['stages'][number]): ScenarioView[] =>
  stage.scenarios && stage.scenarios.length > 0
    ? stage.scenarios
    : [{ when: 'ALWAYS', action: stage.action === 'ApproveAndFinish' ? 'ApproveAndFinish' : 'ApproveAndContinue', approvers: stage.approvers }]

const describeCondition = (c: ScenarioCondition): string =>
  c.field === 'total'
    ? `${FIELD_WORDS.total} ${OP_WORDS[c.op] ?? c.op} ₹${String(c.value)}`
    : `${FIELD_WORDS[c.field]} is one of ${Array.isArray(c.value) ? c.value.length : 1}`

const ScenarioTable = ({ scenarios }: { scenarios: readonly ScenarioView[] }) => (
  <table className="scenario-table">
    <thead><tr><th style={{ width: 40 }}>#</th><th>When</th><th>Action</th><th>Who can approve</th></tr></thead>
    <tbody>
      {scenarios.map((s, i) => (
        <tr key={i}>
          <td>{i + 1}</td>
          <td>{s.when === 'ALWAYS' ? 'Always' : `Conditional — ${(s.conditions ?? []).map(describeCondition).join(' and ')}`}</td>
          <td>{ACTION_WORDS[s.action]}</td>
          <td>{s.approvers === 'ANYONE_WITH_PERMISSION' || s.approvers.length === 0 ? 'Anyone with permission' : <Approvers ids={s.approvers} />}</td>
        </tr>
      ))}
    </tbody>
  </table>
)

/* ---- the editor ------------------------------------------------------------ */

interface DraftStep { name: string; scenarios: ScenarioView[] }
interface Draft {
  id: string | null
  docType: string
  docTypeLabel: string
  name: string
  description: string
  steps: DraftStep[]
  appliesTo: string[]
  overrideUserIds: string[]
  alerts: RouteAlert[]
  status: RouteView['status'] | null
}

const anyone: ScenarioView = { when: 'ALWAYS', action: 'ApproveAndFinish', approvers: 'ANYONE_WITH_PERMISSION' }

const blankDraft = (docType: string, docTypeLabel: string): Draft => ({
  id: null, docType, docTypeLabel, name: '', description: '',
  steps: [{ name: 'Approval', scenarios: [anyone] }],
  appliesTo: [], overrideUserIds: [], alerts: [], status: null,
})

const draftOf = (r: RouteView): Draft => ({
  id: r.id, docType: r.docType, docTypeLabel: r.docTypeLabel, name: r.name, description: r.description ?? '',
  steps: r.stages.map((s) => ({ name: s.name, scenarios: scenariosOf(s) })),
  appliesTo: r.appliesTo, overrideUserIds: r.overrideUserIds, alerts: r.alerts, status: r.status,
})

export const RouteEditor = ({ draft: initial, onDone }: { draft: Draft; onDone: (id: string | null) => void }) => {
  const api = useApi()
  const admin = adminApi(api)
  const client = useQueryClient()
  const [d, setD] = useState<Draft>(initial)
  const [error, setError] = useState<string | null>(null)
  const users = useQuery({ queryKey: ['adminUsers', api.companyId], queryFn: () => api.adminUsers() })
  const alertActions = useMemo(() => alertActionsFor(d.docType), [d.docType])

  const save = useMutation({
    mutationFn: (asDraft: boolean) => {
      const input: RouteInput = {
        docType: d.docType, name: d.name, description: d.description.trim() || null,
        stages: d.steps.filter((s) => s.name.trim() !== '').map((s) => ({
          name: s.name, action: s.scenarios[s.scenarios.length - 1]?.action ?? 'ApproveAndContinue', scenarios: s.scenarios,
        })),
        asDraft, appliesTo: d.appliesTo, overrideUserIds: d.overrideUserIds,
        alerts: d.alerts.filter((a) => a.projectRoleIds.length + a.roleIds.length + a.userIds.length > 0),
      }
      return admin.saveRoute(input)
    },
    onSuccess: (next) => {
      client.setQueryData(routesKey(api.companyId), next)
      const created = next.filter((r) => r.docType === d.docType && r.companyId === api.companyId)
        .sort((a, b) => b.version - a.version)[0]
      onDone(created?.id ?? null)
    },
    onError: (err) => setError(messageFor(err)),
  })

  const setStep = (i: number, step: DraftStep): void => {
    const steps = [...d.steps]
    steps[i] = step
    setD({ ...d, steps })
  }
  const move = (i: number, by: -1 | 1): void => {
    const steps = [...d.steps]
    const j = i + by
    if (j < 0 || j >= steps.length) return
    ;[steps[i], steps[j]] = [steps[j]!, steps[i]!]
    setD({ ...d, steps })
  }
  const alertFor = (action: string): RouteAlert =>
    d.alerts.find((a) => a.action === action) ?? { action, projectRoleIds: [], roleIds: [], userIds: [] }
  const setAlert = (next: RouteAlert): void =>
    setD({ ...d, alerts: [...d.alerts.filter((a) => a.action !== next.action), next] })

  return (
    <>
      <button type="button" className="crumb link-button" onClick={() => onDone(initial.id)}><Icon name="left" size={15} /> Back</button>
      <h2 className="page-title">{initial.id ? 'Edit workflow' : 'New workflow'} · {d.docTypeLabel}</h2>
      {error ? <div style={{ margin: '12px 0' }}><Notice kind="bad">{error}</Notice></div> : null}

      <Panel title="General Info">
        <div className="form-grid">
          <Field label="Document" hint="Fixed once created."><Input value={d.docTypeLabel} onChange={() => undefined} disabled /></Field>
          <Field label="Name" hint="For your own reference, on the audit trail."><Input value={d.name} onChange={(name) => setD({ ...d, name })} /></Field>
        </div>
        <Field label="Description">
          <textarea className="control" rows={3} value={d.description} aria-label="Description"
            onChange={(e) => setD({ ...d, description: e.currentTarget.value })} />
        </Field>
      </Panel>

      <Panel title="Approval Steps" aside="Scenarios are read in order; the first that applies and that the signer may sign decides">
        {d.steps.map((step, i) => (
          <div className="route-step" key={i}>
            <div className="row" style={{ alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <Field label={`Step ${i + 1}`}><Input value={step.name} onChange={(name) => setStep(i, { ...step, name })} /></Field>
              </div>
              <Button kind="ghost" aria-label={`Move step ${i + 1} up`} disabled={i === 0} onClick={() => move(i, -1)}><Icon name="up" size={16} /></Button>
              <Button kind="ghost" aria-label={`Move step ${i + 1} down`} disabled={i === d.steps.length - 1} onClick={() => move(i, 1)}><Icon name="down" size={16} /></Button>
              <Button kind="ghost" disabled={d.steps.length === 1} onClick={() => setD({ ...d, steps: d.steps.filter((_, k) => k !== i) })}>Remove</Button>
            </div>
            <table className="scenario-table">
              <thead><tr><th style={{ width: 40 }}>#</th><th>When</th><th style={{ width: 220 }}>Action</th><th>Who can approve</th><th style={{ width: 90 }} /></tr></thead>
              <tbody>
                {step.scenarios.map((sc, k) => (
                  <ScenarioRow key={k} index={k} stepIndex={i} scenario={sc}
                    onChange={(next) => { const s = [...step.scenarios]; s[k] = next; setStep(i, { ...step, scenarios: s }) }}
                    onRemove={step.scenarios.length > 1 ? () => setStep(i, { ...step, scenarios: step.scenarios.filter((_, x) => x !== k) }) : null} />
                ))}
              </tbody>
            </table>
            <Button kind="ghost" icon={<Icon name="plus" size={16} />} disabled={step.scenarios.length >= 6}
              onClick={() => setStep(i, { ...step, scenarios: [...step.scenarios, { when: 'ALWAYS', action: 'ApproveAndContinue', approvers: 'ANYONE_WITH_PERMISSION' }] })}>
              Add scenario to this step
            </Button>
          </div>
        ))}
        <Button kind="ghost" icon={<Icon name="plus" size={18} />} disabled={d.steps.length >= 12}
          onClick={() => setD({ ...d, steps: [...d.steps, { name: '', scenarios: [anyone] }] })}>Add step</Button>
      </Panel>

      <Panel title="Override">
        <Field label="Override Users" hint="They may sign any step; signing as one finishes the route.">
          <ChipChoice label="Override Users" value={d.overrideUserIds} onChange={(overrideUserIds) => setD({ ...d, overrideUserIds })}
            options={(users.data ?? []).filter((u) => u.active).map((u) => ({ value: u.id, label: u.name }))} />
        </Field>
      </Panel>

      <Panel title="Alerts" aside="Who is told when each of these happens">
        <table className="scenario-table">
          <thead><tr><th>On Action</th><th>Project Roles</th><th>Roles</th><th>Users</th></tr></thead>
          <tbody>
            {alertActions.map((a) => {
              const row = alertFor(a.action)
              return (
                <tr key={a.action}>
                  <td>{a.label}</td>
                  <td><MultiLookup entity="project_role" value={row.projectRoleIds} onChange={(projectRoleIds) => setAlert({ ...row, projectRoleIds })} /></td>
                  <td><MultiLookup entity="app_role" value={row.roleIds} onChange={(roleIds) => setAlert({ ...row, roleIds })} /></td>
                  <td>
                    <ChipChoice label={`Users told on ${a.label}`} value={row.userIds} onChange={(userIds) => setAlert({ ...row, userIds })}
                      options={(users.data ?? []).filter((u) => u.active).map((u) => ({ value: u.id, label: u.name }))} />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </Panel>

      <div className="row" style={{ margin: '20px 0' }}>
        <Button kind="ghost" onClick={() => onDone(initial.id)}>Cancel</Button>
        <Button kind="ghost" disabled={save.isPending} onClick={() => save.mutate(true)}>Save as a draft</Button>
        <Button kind="primary" disabled={save.isPending} onClick={() => save.mutate(false)}>Publish</Button>
      </div>
      <Notice kind="info">
        Publishing makes a new version. Documents already in review keep the route they entered; override users and
        companies changed from the Action menu apply at once.
      </Notice>
    </>
  )
}

const ScenarioRow = ({ index, stepIndex, scenario, onChange, onRemove }: {
  index: number; stepIndex: number; scenario: ScenarioView
  onChange: (next: ScenarioView) => void
  onRemove: (() => void) | null
}) => {
  const named = scenario.approvers === 'ANYONE_WITH_PERMISSION' ? [] : [...scenario.approvers]
  const conditions = scenario.conditions ?? []
  const setCondition = (k: number, c: ScenarioCondition): void => {
    const next = [...conditions]
    next[k] = c
    onChange({ ...scenario, conditions: next })
  }
  const label = `step ${stepIndex + 1} scenario ${index + 1}`
  return (
    <tr>
      <td>{index + 1}</td>
      <td>
        <Select value={scenario.when} aria-label={`When, ${label}`}
          onChange={(when) => onChange(when === 'CONDITIONAL'
            ? { ...scenario, when: 'CONDITIONAL', conditions: conditions.length > 0 ? conditions : [{ field: 'total', op: 'lte', value: '' }] }
            : { when: 'ALWAYS', action: scenario.action, approvers: scenario.approvers })}
          options={[{ value: 'ALWAYS', label: 'Always' }, { value: 'CONDITIONAL', label: 'Conditional' }]} />
        {scenario.when === 'CONDITIONAL' ? conditions.map((c, k) => (
          <div key={k} style={{ marginTop: 8, display: 'grid', gap: 6 }}>
            <Select value={c.field} aria-label={`Condition on, ${label}`}
              onChange={(field) => setCondition(k, field === 'total'
                ? { field: 'total', op: 'lte', value: '' }
                : { field: field as ScenarioCondition['field'], op: 'in', value: [] })}
              options={Object.entries(FIELD_WORDS).map(([value, l]) => ({ value, label: l }))} />
            {c.field === 'total' ? (
              <div className="row">
                <Select value={c.op} aria-label={`Compared, ${label}`} onChange={(op) => setCondition(k, { ...c, op })}
                  options={['lte', 'lt', 'gte', 'gt'].map((op) => ({ value: op, label: OP_WORDS[op]! }))} />
                <Input value={String(c.value)} aria-label={`Amount, ${label}`} inputMode="decimal"
                  onChange={(value) => setCondition(k, { ...c, value: value.replace(/[^0-9.]/g, '') })} />
              </div>
            ) : (
              <MultiLookup entity={ENTITY_FOR[c.field]!} value={Array.isArray(c.value) ? c.value : []}
                onChange={(ids) => setCondition(k, { ...c, value: ids })} />
            )}
          </div>
        )) : null}
      </td>
      <td>
        <Select value={scenario.action} aria-label={`Action, ${label}`} onChange={(action) => onChange({ ...scenario, action: action as ScenarioAction })}
          options={[{ value: 'ApproveAndContinue', label: ACTION_WORDS.ApproveAndContinue }, { value: 'ApproveAndFinish', label: ACTION_WORDS.ApproveAndFinish }]} />
      </td>
      <td>
        <MultiLookup entity="employee" value={named} placeholder="Anyone with permission — or name people"
          onChange={(ids) => onChange({ ...scenario, approvers: ids.length > 0 ? ids : 'ANYONE_WITH_PERMISSION' })} />
      </td>
      <td>{onRemove ? <Button kind="ghost" onClick={onRemove}>Remove</Button> : null}</td>
    </tr>
  )
}
